import React from "react";
import { Table, EmptyState, Input } from "../../components/ui";
import { Users } from "lucide-react";

/**
 * Student marks grid — marks obtained / full / absent only.
 */
export default function MarksEntryTable({
  rows = [],
  maxMarks = 0,
  locked = false,
  onChange,
}) {
  const updateRow = (index, patch) => {
    if (locked) return;
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange?.(next);
  };

  if (!rows.length) {
    return (
      <div className="py-8">
        <EmptyState
          icon={Users}
          title="No students found"
          description="There are no students registered for this mark schedule."
        />
      </div>
    );
  }

  const columns = [
    {
      key: "rollNumber",
      header: "#",
      width: "50px",
      render: (row, i) => (
        <span className="text-neutral-400 dark:text-neutral-500 font-mono text-xs">
          {row.student?.rollNumber || i + 1}
        </span>
      ),
    },
    {
      key: "student",
      header: "Student",
      render: (row) => {
        const student = row.student || {};
        const displayName =
          student.name ||
          [student.firstName, student.middleName, student.lastName]
            .filter(Boolean)
            .join(" ") ||
          "—";
        return (
          <div>
            <div className="font-medium text-neutral-900 dark:text-neutral-100">{displayName}</div>
            <div className="text-xs text-neutral-400 dark:text-neutral-500">
              {student.admissionNo || student.studentId || ""}
            </div>
          </div>
        );
      },
    },
    {
      key: "isAbsent",
      header: "Absent",
      width: "100px",
      render: (row, i) => (
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            disabled={locked}
            checked={Boolean(row.isAbsent)}
            onChange={(e) =>
              updateRow(i, {
                isAbsent: e.target.checked,
                marksObtained: e.target.checked ? 0 : row.marksObtained,
              })
            }
            className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-700 text-primary-900 dark:text-primary-300 focus:ring-primary-500 disabled:opacity-50"
          />
        </label>
      ),
    },
    {
      key: "marksObtained",
      header: `Marks / ${maxMarks}`,
      width: "160px",
      render: (row, i) => (
        <Input
          type="number"
          min={0}
          max={maxMarks}
          step="0.5"
          disabled={locked || row.isAbsent}
          value={
            row.isAbsent
              ? ""
              : row.marksObtained === null || row.marksObtained === undefined
              ? ""
              : row.marksObtained
          }
          onChange={(e) => {
            const v = e.target.value;
            updateRow(i, {
              marksObtained: v === "" ? null : Number(v),
              isAbsent: false,
            });
          }}
          className="w-28"
        />
      ),
    },
  ];

  return (
    <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden bg-white dark:bg-neutral-900">
      <Table columns={columns} data={rows} />
    </div>
  );
}